import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { useConnections } from '@/features/connections';
import { useReadiness } from '@/features/platform-status';
import { BindingScheduling, isDurable, schedulerHealth } from '@/features/scheduling';
import { SCREEN_PATHS } from '@/shared/routes';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { ConfirmDialog } from '@/shared/ui/ConfirmDialog';
import { DataTable, PrimaryCell } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { LifecycleStrip } from '@/shared/ui/LifecycleStrip';
import { SkeletonRows } from '@/shared/ui/Skeleton';
import { StatusBadge } from '@/shared/ui/StatusBadge';

import type { Rule } from '@/shared/api/generated/rules';
import { ApiError } from '@/shared/api/http';
import { TextField } from '@/shared/ui/Field';

import {
  bindingsBlockingDelete,
  useActivateRule,
  useBindings,
  useDeleteRule,
  useExecutions,
  useRule,
  useRuleVersions,
  useRules,
  useUpdateRule,
} from '../api/rules';
import {
  LIFECYCLE_STAGES,
  lastRunOf,
  reachOf,
  ruleTone,
  severityTone,
  standingOf,
} from '../model/presentation';
import { PublishVersionForm } from './RulesScreen';
import { TransitionApproval } from './TransitionApproval';

/* One rule: what it is, where it is in the lifecycle, what it applies to, and what can be done to it.
 *
 * **Why this exists rather than more controls in the list.** The Rules table carried a `Publish` and
 * an `Activate` button per row, and activation is a two-eyes transition — the platform refuses it with
 * a 409 whose reason is "a second person is missing". A list row is the wrong place for that: it has
 * no room for the reason, no room for the version history that says what is being activated, and it
 * puts a governed transition one stray click from a mouse resting on the wrong line. The list now
 * carries `Open`, matching the reference, and this is where `Open` goes.
 *
 * **The lifecycle strip here is hollow past the rule's own position**, which is the variant
 * `LifecycleStrip` documents and the Rules screen deliberately does not use: there, every step is
 * drawn as reached because the strip is the product's lifecycle. Here the subject is one rule, so
 * not-yet is an absence and renders as one.
 *
 * Every read on this screen is one the Rules screen already makes, so arriving through `Open` costs
 * nothing — the rule, its bindings and the recent executions all come from cache.
 */

/* Editing a rule's metadata: its name, owner, description and tags.
 *
 * **Nothing here can change what the rule measures, and the type is the reason.** `RuleEdit` has no
 * expression field and the route's body schema is `additionalProperties: false` over the same four
 * keys, so a version cannot be reached from this form however it is edited later. Changing the
 * expression is `PublishVersionForm` below, which appends an immutable version — that split is
 * `Rule` vs `RuleVersion` surfacing in the UI, and the panels sit next to each other so the choice
 * is visible rather than something a reader has to know.
 *
 * An inline `Card` rather than a modal, matching `PublishVersionForm`: the current values are the
 * starting point of the edit, so the form wants to sit beside the rule it is editing rather than
 * cover it.
 */
function EditRuleForm({
  rule,
  onDone,
}: {
  readonly rule: Rule;
  readonly onDone: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const update = useUpdateRule();

  /* Seeded from the rule, which is what makes this an *edit* rather than a re-entry — the same
   * reasoning `PublishVersionForm` carries for its parameters. Keyed on `rule.id` via the parent's
   * conditional render, so opening the form for a different rule starts from that rule's values. */
  const [name, setName] = useState(rule.name);
  const [owner, setOwner] = useState(rule.owner);
  const [description, setDescription] = useState(rule.description ?? '');
  const [tagsStr, setTagsStr] = useState(rule.tags.join(', '));

  const trimmedName = name.trim();
  const trimmedOwner = owner.trim();
  /* Refused here rather than sent, matching `PublishVersionForm`'s parameter check: the server's
   * refusal is correct and a reader answers it far better beside the field than as a banner over a
   * form whose field they then have to find. The server still checks — this is the affordance
   * agreeing with it, not guarding it. */
  const incomplete = trimmedName === '' || trimmedOwner === '';

  const handleSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    if (incomplete) return;
    update.mutate(
      {
        ruleId: rule.id,
        name: trimmedName,
        owner: trimmedOwner,
        /* Empty means cleared, never omitted. The route is a full replacement and says so, so
         * sending `null` for a field the steward emptied is the honest reading — leaving it out
         * would mean the same thing and read as an oversight. */
        description: description.trim() === '' ? null : description.trim(),
        tags: tagsStr
          .split(',')
          .map((tag) => tag.trim())
          .filter(Boolean),
      },
      { onSuccess: onDone },
    );
  };

  return (
    <Card title={t('ruleDetail.edit.title')} hint={t('ruleDetail.edit.hint')}>
      <form onSubmit={handleSubmit}>
        {update.isError && (
          <div style={{ marginBottom: 'var(--space-3)' }}>
            {/* A 409 is the expected answer for two ordinary mistakes — a name already taken, or a
                deprecated rule — so it is a notice rather than an error, and the server's sentence
                names which. Every other status keeps the error tone. */}
            <Alert
              tone={
                update.error instanceof ApiError && update.error.status === 409
                  ? 'notice'
                  : 'error'
              }
              title={t('ruleDetail.edit.refusedTitle')}
            >
              {update.error.message}
            </Alert>
          </div>
        )}
        <div className="lq-grid">
          <TextField
            label={t('rules.field.name')}
            value={name}
            onChange={setName}
            required
            hint={t('ruleDetail.edit.nameHint')}
          />
          <TextField
            label={t('rules.field.owner')}
            value={owner}
            onChange={setOwner}
            required
            hint={t('ruleDetail.edit.ownerHint')}
          />
          <TextField
            label={t('ruleDetail.edit.descriptionLabel')}
            value={description}
            onChange={setDescription}
            hint={t('ruleDetail.edit.descriptionHint')}
          />
          <TextField
            label={t('ruleDetail.edit.tagsLabel')}
            value={tagsStr}
            onChange={setTagsStr}
            placeholder={t('ruleDetail.edit.tagsPlaceholder')}
            hint={t('ruleDetail.edit.tagsHint')}
          />
        </div>
        <div
          style={{
            marginTop: 'var(--space-4)',
            display: 'flex',
            gap: 'var(--space-2)',
          }}
        >
          <Button
            tone="primary"
            type="submit"
            busy={update.isPending}
            disabled={incomplete}
            title={
              incomplete
                ? t('ruleDetail.edit.incomplete')
                : t('ruleDetail.edit.submitMeaning')
            }
          >
            {t('ruleDetail.edit.submit')}
          </Button>
          <Button onClick={onDone}>{t('ruleDetail.edit.cancel')}</Button>
        </div>
      </form>
    </Card>
  );
}

export function RuleDetail(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { ruleId = '' } = useParams<{ ruleId: string }>();

  const rule = useRule(ruleId);
  const versions = useRuleVersions(ruleId);
  const bindings = useBindings();
  /* The tenant's sources, for the harvest trigger's picker and for naming the one it holds.
   *
   * Read once here and handed down, because the alternative is one request per binding on a rule
   * with several — and the API returns only a `connection_id`, so a card with no list would render
   * a uuid in the sentence a reader is meant to understand. */
  const sources = useConnections();
  const connectionOptions = (sources.data?.items ?? []).map((each) => ({
    id: each.id,
    name: each.name,
  }));

  /* Whether scheduled work survives a restart of the replica that accepted it. `/readiness` sends
   * only the degraded capabilities, so `production` is an absence — and a failed read is `unknown`,
   * which is not durable: a card must not promise durability this screen could not verify. */
  const readiness = useReadiness();
  const durable = isDurable(
    schedulerHealth(readiness.isSuccess ? readiness.data.degraded : null),
    'rule_execution_scheduler',
  );
  const executions = useExecutions();
  /* The same bounded search read the list makes, so `reachOf` and `lastRunOf` see exactly what they
   * see there and the two screens cannot report different figures for one rule. */
  const rules = useRules('');
  const activate = useActivateRule();
  const deleteRule = useDeleteRule();
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [editing, setEditing] = useState(false);

  if (rule.isPending) {
    return (
      <>
        <p role="status" className="lq-muted">
          {t('ruleDetail.loading')}
        </p>
        <SkeletonRows rows={4} columns={2} />
      </>
    );
  }

  if (rule.isError) {
    return (
      <>
        <Alert tone="error" title={t('ruleDetail.unreachable')}>
          {rule.error.message}
        </Alert>
        <p style={{ marginTop: 'var(--space-3)' }}>
          <Link to={SCREEN_PATHS.rules}>{t('ruleDetail.backToRules')}</Link>
        </p>
      </>
    );
  }

  const own = rule.data;
  const ownBindings = (bindings.data?.items ?? []).filter(
    (binding) => binding.rule_id === own.id,
  );
  const blockingBindings = bindingsBlockingDelete(deleteRule.error);
  const reach = reachOf(
    own,
    bindings.isSuccess ? bindings.data.items : undefined,
    executions.isSuccess ? executions.data.items : undefined,
  );
  const lastRun = lastRunOf(
    own,
    bindings.isSuccess ? bindings.data.items : undefined,
    executions.isSuccess ? executions.data.items : undefined,
  );

  /* **The rule's position, not the product's.** A rule in `draft` has reached `Draft` and nothing
   * else, so the four stages after it are hollow — including `Published`, which is hollow even for a
   * draft that *has* a published version, because the strip tracks the rule and publishing does not
   * move it. That is the honest reading and it is why the versions table sits directly below: it is
   * where "a version exists" is answerable. */
  const reachedThrough = own.state === 'deprecated' ? 5 : own.state === 'active' ? 4 : 1;

  return (
    <section aria-labelledby="rule-heading">
      <nav aria-label={t('breadcrumbs.label')} className="lq-breadcrumbs">
        <Link to={SCREEN_PATHS.rules}>{t('nav.rules')}</Link>
        <span aria-hidden="true" className="lq-breadcrumbs__sep">
          /
        </span>
        <span aria-current="page">{own.name}</span>
      </nav>

      <div className="lq-page-header">
        <div>
          <h2 id="rule-heading" className="lq-page-header__title">
            {own.name}
          </h2>
          <p className="lq-page-header__lede">
            {[
              t(`rules.kind.${own.kind}`),
              own.owner,
              reach.kind === 'resolved'
                ? t('rules.reach.resolved.label', { count: reach.datasets })
                : reach.kind === 'unbound'
                  ? t('ruleDetail.unbound')
                  : null,
              /* The date only. `rules.lastRun.ran.label` carries a dataset count too, and the reach
                 clause above already states it — the list needs both because it has no reach column
                 beside the date, and here they would be the same number twice. */
              lastRun.kind === 'ran'
                ? t('ruleDetail.lastRun', { at: new Date(lastRun.at) })
                : null,
            ]
              .filter((clause): clause is string => clause !== null)
              .join(' · ')}
          </p>
          {own.description === null ? null : (
            <p className="lq-muted">{own.description}</p>
          )}
        </div>
        <div className="lq-page-header__actions">
          <StatusBadge
            tone={ruleTone(own.state)}
            label={t(`rules.state.${own.state}.label`)}
            title={t(`rules.state.${own.state}.meaning`)}
          />
          {/* **Only for a draft, and disabled otherwise rather than hidden.** A control that vanishes
              leaves a reader wondering whether they lack the permission or the rule lacks the state;
              disabled with the reason on it says which. The platform refuses the transition anyway —
              this is the affordance agreeing with the server rather than guarding it. */}
          <Button
            tone="primary"
            busy={activate.isPending}
            disabled={own.state !== 'draft'}
            title={
              own.state === 'draft'
                ? t('ruleDetail.activate.meaning')
                : t(`ruleDetail.activate.refused.${own.state}`)
            }
            onClick={() => {
              activate.mutate(own.id);
            }}
          >
            {t('rules.activate.action')}
          </Button>
          {/* **Disabled for a deprecated rule rather than hidden**, matching Activate's reasoning:
              a control that vanishes leaves a reader unsure whether they lack the permission or the
              rule lacks the state. Deprecation is terminal, so its name and owner are kept as the
              historical record they are and the server answers 409 — this says so before the round
              trip. */}
          <Button
            disabled={own.state === 'deprecated'}
            title={
              own.state === 'deprecated'
                ? t('ruleDetail.edit.refused.deprecated')
                : t('ruleDetail.edit.meaning')
            }
            onClick={() => {
              setEditing((open) => !open);
            }}
          >
            {t('ruleDetail.edit.action')}
          </Button>
          {/* No state gate, unlike Activate: `RuleService.delete` refuses on *live bindings*, not
              on state, and the first press is how a caller finds out whether there are any — a
              disabled-until-checked button would need the same round trip anyway. */}
          <Button
            tone="danger"
            title={t('ruleDetail.delete.meaning')}
            onClick={() => {
              setConfirmingDelete(true);
            }}
          >
            {t('ruleDetail.delete.action')}
          </Button>
        </div>
      </div>

      {activate.isError && (
        /* A 409 here is the two-eyes refusal and is the expected answer for a first press, so it is a
           notice rather than an error: the message names what is missing, which is a second person. */
        <Alert tone="notice" title={t('ruleDetail.activate.refusedTitle')}>
          {activate.error.message}
        </Alert>
      )}

      {/* **Two presses when the rule has live bindings, and the second one carries a number.**
          `ConnectionDetail`'s cascade-delete shape exactly: the first press asks for the plain
          delete; if the rule has live bindings the server refuses and *states how many*, so this
          dialog re-asks offering to disable exactly that many with it. The number confirmed back
          is the one the server reported microseconds earlier, which is what makes the server's own
          count check pass rather than racing a stale screen. */}
      <ConfirmDialog
        open={confirmingDelete}
        title={t('ruleDetail.delete.confirmTitle')}
        message={
          blockingBindings === null
            ? t('ruleDetail.delete.confirmBody', { name: own.name })
            : t('ruleDetail.delete.confirmBodyWithBindings', {
                name: own.name,
                bindings: blockingBindings,
              })
        }
        confirmLabel={
          blockingBindings === null
            ? t('ruleDetail.delete.action')
            : t('ruleDetail.delete.actionWithBindings', { bindings: blockingBindings })
        }
        cancelLabel={t('common.cancel')}
        tone="danger"
        busy={deleteRule.isPending}
        /* The refusal that *offers* the cascade is not shown as an error — it is the question
           being re-asked with a number in it. Every other refusal, including a stale count, still
           shows. */
        error={
          deleteRule.isError && blockingBindings === null
            ? deleteRule.error.message
            : undefined
        }
        onConfirm={() => {
          deleteRule.mutate(
            blockingBindings === null
              ? { ruleId: own.id }
              : { ruleId: own.id, retireBindings: blockingBindings },
            {
              onSuccess: () => {
                setConfirmingDelete(false);
                /* The page below this dialog reads a rule that no longer exists — staying here
                   would mean the next render's `useRule` refetch turns into a 404 the reader has
                   to make sense of. Back to the list, where the row is simply gone now. */
                navigate(SCREEN_PATHS.rules);
              },
            },
          );
        }}
        onCancel={() => {
          setConfirmingDelete(false);
          /* Resets the mutation, so re-opening the dialog does not show the previous refusal —
             `ConnectionDetail`'s reason: a reader who cancels and reconsiders should not be met
             by an error about the attempt they abandoned. */
          deleteRule.reset();
        }}
      />

      <div className="lq-stack">
        <LifecycleStrip
          steps={LIFECYCLE_STAGES.map((stage, index) => ({
            label: t(stage.labelKey),
            reached: index < reachedThrough,
            colour: stage.hue,
          }))}
          note={t('rules.lifecycle.note')}
        />

        {/* Above the approval and the version form, because it is the cheapest of the three and the
            one with no governance attached: renaming a rule needs nobody's signature. Keyed on the
            rule id so switching rules restarts the form from that rule's values rather than keeping
            the previous one's. */}
        {editing ? (
          <EditRuleForm
            key={own.id}
            rule={own}
            onDone={() => {
              setEditing(false);
            }}
          />
        ) : null}

        {/* **The approval, above publishing.** The order is the order of the work: a rule needs a
            version before it can be activated, but the *request* can be open while the version is
            still being decided — and this is the panel the activation refusal has been pointing at.
            It renders nothing for a rule that is not a draft. */}
        <TransitionApproval rule={own} />

        {/* Publishing lives here, not in the list. A version freezes an expression, so the form
            belongs beside the history of what has already been frozen.

            **Offered for an active rule too, and that was a real gap rather than a widening.**
            `RuleService.publish_version` has no state gate — it checks the kind matches and that the
            semver strictly increases, nothing more — so the server has always accepted a new version
            for an active rule while this screen offered it only for a draft. Revising a live rule is
            the ordinary case ("the pattern was too strict"), and it had no route through the UI at
            all.

            What makes it safe rather than merely permitted is that a binding pins an exact
            `rule_version_id` rather than following "latest": publishing here changes nothing that is
            currently running, and the new version takes effect when somebody rebinds. The hint on
            the panel says so, because a steward pressing this on a live rule needs to know whether
            they have just changed production.

            Still refused for a deprecated rule: it is terminal, and a new version of a check nobody
            is watching is a new rule. */}
        {own.state !== 'deprecated' ? (
          <PublishVersionForm
            rule={own}
            onPublished={() => {
              /* No local state to clear: `usePublishVersion` invalidates the versions query, so the
                 table below re-reads itself and the form's own suggested version moves on. */
            }}
          />
        ) : null}

        <Card
          title={t('ruleDetail.versions.title')}
          hint={t('ruleDetail.versions.hint')}
          flush
        >
          {versions.isError ? (
            <div className="lq-card__body">
              <Alert tone="error">{versions.error.message}</Alert>
            </div>
          ) : versions.isSuccess && versions.data.items.length === 0 ? (
            <div className="lq-card__body">
              <p className="lq-muted" style={{ margin: 0 }}>
                {t('ruleDetail.versions.empty')}
              </p>
            </div>
          ) : versions.isSuccess ? (
            <DataTable
              caption={t('ruleDetail.versions.caption')}
              columns={[
                { key: 'version', label: t('ruleDetail.versions.column.version') },
                { key: 'columns', label: t('ruleDetail.versions.column.columns') },
                { key: 'published', label: t('ruleDetail.versions.column.published') },
              ]}
            >
              {versions.data.items.map((version) => (
                <tr key={version.id}>
                  <td>
                    <PrimaryCell secondary={version.notes ?? undefined}>
                      <span className="lq-mono">{version.version}</span>
                    </PrimaryCell>
                  </td>
                  <td className="lq-mono">
                    {version.expression.columns.length === 0
                      ? t('ruleDetail.versions.wholeDataset')
                      : version.expression.columns.join(', ')}
                  </td>
                  <td className="lq-dim">
                    {t('ruleDetail.versions.publishedBy', {
                      who: version.published_by,
                      at: new Date(version.published_at),
                    })}
                  </td>
                </tr>
              ))}
            </DataTable>
          ) : (
            <SkeletonRows rows={2} columns={3} />
          )}
        </Card>

        <Card title={t('ruleDetail.bindings.title')} flush>
          {ownBindings.length === 0 ? (
            <div className="lq-card__body">
              <EmptyState
                title={t('ruleDetail.bindings.emptyTitle')}
                guidance={t('ruleDetail.bindings.emptyGuidance')}
                action={
                  <Link className="lq-btn lq-btn--primary" to={SCREEN_PATHS.rules}>
                    {t('rules.bindings.create.open')}
                  </Link>
                }
              />
            </div>
          ) : (
            <DataTable
              caption={t('ruleDetail.bindings.caption')}
              columns={[
                { key: 'target', label: t('rules.bindings.column.target') },
                { key: 'severity', label: t('ruleDetail.bindings.column.severity') },
                { key: 'standing', label: t('rules.bindings.column.standing') },
              ]}
            >
              {ownBindings.map((binding) => {
                const standing = standingOf(
                  binding,
                  rules.data?.items.find((each) => each.id === binding.rule_id) ?? own,
                  executions.isSuccess ? executions.data.items : undefined,
                );
                return (
                  <tr key={binding.id}>
                    <td className="lq-mono">
                      {binding.selectors
                        .map((selector) => selector.description)
                        .join(' or ')}
                    </td>
                    <td>
                      <StatusBadge
                        tone={severityTone(binding.severity)}
                        label={t(`rules.severity.${binding.severity}.label`)}
                        title={t(`rules.severity.${binding.severity}.meaning`)}
                      />
                    </td>
                    <td className="lq-muted">
                      {standing.kind === 'running'
                        ? t('rules.bindings.standing.running', {
                            at: new Date(standing.at),
                          })
                        : standing.kind === 'disabled'
                          ? t('rules.bindings.standing.disabled.label')
                          : standing.kind === 'notRunYet'
                            ? t('rules.bindings.standing.notRunYet.label')
                            : standing.kind === 'ruleNotActive'
                              ? t(`rules.bindings.standing.notActive.${standing.state}`)
                              : t('rules.bindings.standing.unread.label')}
                    </td>
                  </tr>
                );
              })}
            </DataTable>
          )}
        </Card>

        {/* **Two scheduling cards per binding, and they are two independent affordances.** A
            binding may carry a nightly execution schedule *and* a harvest dependency, one, or
            neither — the platform stores them in two tables precisely because one row with a mode
            enum cannot express "both", and a segmented control here would make the commonest useful
            arrangement unreachable.

            Here rather than inside the table above: the forms, the seven facts and every state do
            not fit a cell, and the compact two-line summary on the Rules screen's bindings tab is
            what a reader scanning many bindings gets instead. */}
        {ownBindings.map((binding) => (
          <Card
            key={`scheduling-${binding.id}`}
            title={t('ruleDetail.scheduling.title')}
            hint={binding.selectors.map((selector) => selector.description).join(' or ')}
          >
            <BindingScheduling
              bindingId={binding.id}
              connections={connectionOptions}
              durable={durable}
            />
          </Card>
        ))}
      </div>
    </section>
  );
}
